// Compact USD / CAD / EUR / GBP pill — pairs with LanguageSwitcher.
// Auto-detects from browser locale on first visit; explicit choice
// persists via lib/money.setDisplayCurrency.

import { useEffect, useState } from 'react';
import { Coins } from 'lucide-react';
import { cn } from '../../lib/cn';
import { getDisplayCurrency, setDisplayCurrency, SUPPORTED_CURRENCIES, type DisplayCurrency } from '../../lib/money';

interface Props {
  className?: string;
  variant?: 'compact' | 'labeled';
}

export function CurrencySwitcher({ className, variant = 'compact' }: Props) {
  const [current, setCurrent] = useState<DisplayCurrency>('USD');

  useEffect(() => { setCurrent(getDisplayCurrency()); }, []);

  const pick = (c: DisplayCurrency) => {
    setDisplayCurrency(c);
    setCurrent(c);
    // Hard reload — keeps price formatting consistent across the whole
    // page without piping a context everywhere. Cheap; we render fast.
    if (typeof window !== 'undefined') window.location.reload();
  };

  return (
    <div className={cn('inline-flex items-center gap-1.5', className)}>
      {variant === 'labeled' && (
        <span className="inline-flex items-center gap-1 text-[12px] text-slate-500">
          <Coins className="w-3.5 h-3.5" /> {current}
        </span>
      )}
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">
        {SUPPORTED_CURRENCIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => pick(c)}
            aria-pressed={current === c}
            className={cn(
              'px-2 py-1 text-[10.5px] font-semibold uppercase tracking-wider rounded-full transition-colors',
              current === c
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:text-slate-900',
            )}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}
